import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GettingStartedProgressDto } from './useGettingStartedOnboarding';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { useSocket } from '@/Domain/components/SocketProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { parseHost } from '@/Domain/helper/scrapeUrlClassifier';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  setSiteMapPending,
  SITEMAP_CONSUMED_KEY,
} from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';

// Idle give-up window, re-armed on every backend progress event (so a reconnect or a streaming run resets the clock). Must stay above the backend TASK budget, not just the scrape budget: onboard_from_url_background is `@background_decorator(timeout=120)`, and both the scrape (`url_type_detected`→`article_created`) and the LLM settings call (`settings_generating`→`setting_applied`, `timeout=300s`) are single silent phases with no re-arming event, so a within-budget run can legitimately go quiet for most of those 120s. 150s keeps the original proven floor above that budget so a healthy run is never false-flagged; genuinely-sooner detection needs backend heartbeats (tracked follow-up).
export const IDLE_TIMEOUT_MS = 150_000;

/**
 * Refresh-resume signal: when the cached progress row shows
 * `knowledge_base_step_completed=true` and `automatic_flow_completed=false`,
 * the backend scrape completed in a previous session and the user was
 * somewhere in the post-step wizard (summary / external_apps / deployment)
 * when they reloaded. The hook initializes to `completed` in that case so
 * they resume inside the wizard instead of seeing the URL form again.
 *
 * The `knowledge_base_step_completed` flag is co-opted here because in the
 * automatic flow the scrape IS the knowledge-base setup — the user hands us
 * a URL, we ingest it, and the agent is configured off it. That makes the
 * existing flag semantically accurate for the automatic path without needing
 * a new column.
 */
function isAutomaticResumeable(progress: GettingStartedProgressDto | null | undefined): boolean {
  if (!progress) return false;
  return progress.knowledge_base_step_completed && !progress.automatic_flow_completed;
}

// Falls back to a substring on unparseable input so `url_host` stays non-null
// in PostHog (real events were landing with null and breaking funnel joins).
export function extractUrlHost(url: string): string | undefined {
  if (!url || !url.trim()) return undefined;
  const parsedHost = parseHost(url);
  if (parsedHost) return parsedHost;
  const fallback = url
    .replace(/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//, '')
    .split(/[/?#]/, 1)[0]
    .trim()
    .toLowerCase();
  return fallback || '-invalid-';
}

// Django Ninja serializes backend failures as `{ errors: { root: [{ message, code }] } }`
// (see `ErrorResponse.from_message` in `api/errors/models.py`). Pull out that
// message first so the user sees e.g. the 429 rate-limit copy; fall back to
// axios's own `error.message` ("Network Error", "Request failed with status
// code 500") for non-API failures.
interface ApiErrorResponse {
  errors?: {
    root?: { message: string; code?: string }[];
  };
}

// Coarse-grained error classes attached to `getting_started_automatic_failed`
// for filtering. `network` is the important one — it specifically captures
// the case where the POST never reached our backend (axios `Network Error`
// — DNS, CORS, dropped connection, gateway/edge timeout). Without this
// classification, a network-layer failure looks identical in PostHog to a
// 5xx returned by our own service, even though the diagnostic path is
// totally different. Internal-only — the UI doesn't read it.
type AutoOnboardingErrorClass = 'network' | 'http_4xx' | 'http_5xx' | 'unknown' | 'backend';

// User-facing error categories. Drives the localized message shown in the
// error view. Orthogonal to `error_class` (WHERE the failure happened) — the
// category answers WHY, from the user's perspective. Backend emits these for
// background-task failures; frontend derives them for network/HTTP failures.
export type AutoOnboardingErrorCategory =
  | 'scrape_failed' // URL couldn't be read (invalid, private, blocking automation)
  | 'access_blocked' // all scraping engines failed (HTTP 500), usually bot protection; retry is futile
  | 'timeout' // backend job exceeded its time budget
  | 'dns_failure' // hostname did not resolve (typo, expired domain, DNS not propagated)
  | 'website_not_supported' // Firecrawl returned 403 — host is blocklisted; retry is futile
  | 'bad_request' // 4xx, server rejected the URL itself
  | 'server_error' // 5xx or background-task internal failure
  | 'network' // browser never reached the backend
  | 'unknown'; // catch-all, same copy as server_error

interface ClassifiedError {
  message: string | null;
  errorClass: AutoOnboardingErrorClass;
  errorCategory: AutoOnboardingErrorCategory;
  httpStatus: number | undefined;
}

function classifyMutationError(error: unknown): ClassifiedError {
  if (error instanceof AxiosError) {
    const backendMessage = (error.response?.data as ApiErrorResponse | undefined)?.errors?.root?.[0]
      ?.message;
    const message =
      typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : error.message || null;
    const status = error.response?.status;
    let errorClass: AutoOnboardingErrorClass;
    let errorCategory: AutoOnboardingErrorCategory;
    if (!error.response) {
      // No response = the request failed before getting a status. Axios sets
      // `message = 'Network Error'` for this. Treat it as `network` regardless
      // of the literal so it survives axios version bumps and i18n.
      errorClass = 'network';
      errorCategory = 'network';
    } else if (status && status >= 500) {
      errorClass = 'http_5xx';
      errorCategory = 'server_error';
    } else if (status && status >= 400) {
      errorClass = 'http_4xx';
      errorCategory = 'bad_request';
    } else {
      errorClass = 'unknown';
      errorCategory = 'unknown';
    }
    return { message, errorClass, errorCategory, httpStatus: status };
  }
  const message = error instanceof Error ? error.message || null : null;
  return { message, errorClass: 'unknown', errorCategory: 'unknown', httpStatus: undefined };
}

// Narrows a free-form string from the backend WS payload into a known
// category. Unknown strings (or missing payloads — e.g. older backends) fall
// back to `unknown` so the view layer can render the generic friendly copy.
function parseBackendCategory(raw: string | undefined | null): AutoOnboardingErrorCategory {
  if (
    raw === 'scrape_failed' ||
    raw === 'access_blocked' ||
    raw === 'timeout' ||
    raw === 'dns_failure'
  )
    return raw;
  if (raw === 'website_not_supported') return raw;
  if (raw === 'server_error' || raw === 'bad_request' || raw === 'network') return raw;
  return 'unknown';
}

// Mirrors backend `scrape_rejection_reason` REJECT_* values (firecrawl_client.py); drives the advisory low-quality banner.
export type LowQualityReason =
  | 'http_error'
  | 'soft_404'
  | 'error_page'
  | 'empty_content'
  | 'thin_content'
  | 'off_domain_redirect'
  | 'auth_wall';

function parseWarningReason(raw: string | undefined | null): LowQualityReason | null {
  if (
    raw === 'http_error' ||
    raw === 'soft_404' ||
    raw === 'error_page' ||
    raw === 'empty_content' ||
    raw === 'thin_content' ||
    raw === 'off_domain_redirect' ||
    raw === 'auth_wall'
  ) {
    return raw;
  }
  return null;
}

export type AutoOnboardingStep = {
  step: string;
  // Most fields are strings, but the `completed` event's `summary` is
  // `SummaryItem[]`. Consumers that need that shape cast through `unknown`.
  data?: Record<string, string>;
};

export type AutoOnboardingState = 'idle' | 'processing' | 'completed' | 'error';

export interface UseAutomaticOnboardingResult {
  state: AutoOnboardingState;
  steps: AutoOnboardingStep[];
  /**
   * The user-facing category for the current failure. Drives which localized
   * string the error view renders. `null` outside the `error` state.
   *
   * Deliberately NOT a raw error message — the backend sends `raw_message`
   * separately for logs/PostHog, but we never render it (PR fixing the
   * Firecrawl-error leak). Previous `errorMessage` field was removed for the
   * same reason — consumers should read `errorCategory` only.
   */
  errorCategory: AutoOnboardingErrorCategory | null;
  /**
   * Advisory low-quality-source reason (maintenance page, login wall, thin
   * content). Set independently of `state` — the flow still completes normally
   * with a neutral persona; this only drives a non-blocking warning banner.
   */
  warningReason: LowQualityReason | null;
  urlType: 'shopify' | 'generic' | null;
  submittedUrl: string | null;
  submitUrl: (url: string) => void;
  isSubmitting: boolean;
  reset: () => void;
}

export function useAutomaticOnboarding(): UseAutomaticOnboardingResult {
  const queryClient = useQueryClient();
  // On fresh mount, consult the cached progress row. If the scrape previously
  // completed (kb_step flag set) and the user hasn't finished the wizard yet,
  // rehydrate into `completed` so the component renders the post-step wizard
  // instead of the URL form. The live WS event stream is gone — the summary
  // card degrades to its minimal "ready" branch via the `steps.length === 0`
  // fallback inside `AutomaticOnboarding`.
  const cachedProgress = queryClient.getQueryData<GettingStartedProgressDto | null>([
    QueryKey.gettingStarted.progress,
  ]);
  const resumed = useRef(isAutomaticResumeable(cachedProgress)).current;

  const [state, setState] = useState<AutoOnboardingState>(resumed ? 'completed' : 'idle');
  const [steps, setSteps] = useState<AutoOnboardingStep[]>([]);
  const [errorCategory, setErrorCategory] = useState<AutoOnboardingErrorCategory | null>(null);
  // On refresh-resume the live WS stream (and its low_quality_source event) is
  // gone; rehydrate the advisory from the persisted progress row so a rejected
  // source doesn't silently read as a clean "ready" result.
  const [warningReason, setWarningReason] = useState<LowQualityReason | null>(
    resumed ? parseWarningReason(cachedProgress?.automatic_low_quality_reason) : null,
  );
  const [urlType, setUrlType] = useState<'shopify' | 'generic' | null>(null);
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const { socket } = useSocket();
  const { refreshConfiguration } = usePreview();
  const { capture } = usePostHogAnalytics();
  const stateRef = useRef(state);
  stateRef.current = state;
  const warningReasonRef = useRef(warningReason);
  warningReasonRef.current = warningReason;
  // Running count of `setting_applied` events since the last submit. Reset in
  // `submitUrl` alongside the other per-run state so a retry starts at zero.
  const settingsAppliedCountRef = useRef(0);
  // Snapshot of the URL + detected type for this run so we can attach them to
  // the terminal completed/failed PostHog events. Socket events don't carry
  // the URL back, and `urlType` is state that may not have flushed by the time
  // `completed` arrives — refs sidestep both problems.
  const submittedUrlRef = useRef<string | null>(null);
  const urlTypeRef = useRef<'shopify' | 'generic' | null>(null);
  // Idle watchdog timer id, re-armed on each progress event so only real silence trips it.
  const idleWatchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist the "scrape done" signal on the progress row so a page refresh
  // drops the user back into the post-step wizard. Uses the shared mutation
  // defaults (see queryClient.ts) so we just feed it a delta. On success we
  // merge only the fields we owned into the cache rather than overwrite with
  // the full server response — another concurrent PATCH (e.g. the
  // deployment-step Finish click) may have updated the row between when this
  // request was dispatched and when the server echoed its view back, and a
  // full overwrite would regress those newer fields.
  const progressMutation = useMutation<
    GettingStartedProgressDto,
    Error,
    Partial<GettingStartedProgressDto>
  >({
    mutationKey: [QueryKey.gettingStarted.progress],
    onSuccess: (_updated, variables) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, ...variables } : prev),
      );
    },
  });

  // Terminal "scrape done": refresh caches, enter the post-step wizard, persist the resume flag, and tag PostHog (`recovered` makes silent-stuck recovery measurable).
  const applyCompletion = useCallback(
    (recovered?: 'reconnect' | 'timeout') => {
      // Flip the ref synchronously so a second recovery path (reconnect/watchdog/late event) resolving in the same tick sees `completed` at its guard and bails instead of double-applying before React re-renders.
      stateRef.current = 'completed';
      // Restore the advisory the confetti gate reads at Finish: recovery
      // refetched the row first, while live completion mirrors the terminal
      // event's verdict (or the earlier low_quality_source event from an older
      // backend) into the cache the page already owns.
      if (recovered) {
        const fresh = queryClient.getQueryData<GettingStartedProgressDto | null>([
          QueryKey.gettingStarted.progress,
        ]);
        setWarningReason((prev) => prev ?? parseWarningReason(fresh?.automatic_low_quality_reason));
      } else {
        queryClient.setQueryData<GettingStartedProgressDto | null>(
          [QueryKey.gettingStarted.progress],
          (prev) =>
            prev ? { ...prev, automatic_low_quality_reason: warningReasonRef.current } : prev,
        );
      }
      queryClient.invalidateQueries({ queryKey: ['knowledge_base'] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.widgetSettings] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
      refreshConfiguration();
      setState('completed');
      progressMutation.mutate({ knowledge_base_step_completed: true });
      // Known-generic only: Shopify never maps (a dead pending flag would poll an idle
      // 404), and a recovered run that missed `url_type_detected` (type null) skips
      // rather than flag blindly. A recovered generic run still opens the review — the
      // backend mapped it regardless of whether the client saw the live event.
      if (urlTypeRef.current === 'generic') {
        localStorage.removeItem(SITEMAP_CONSUMED_KEY);
        setSiteMapPending();
        capture(PostHogEventEnum.SiteScrapeMapStarted, {
          source: 'onboarding_auto',
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
      }
      capture(PostHogEventEnum.GettingStartedAutomaticCompleted, {
        url_type: urlTypeRef.current,
        settings_count: settingsAppliedCountRef.current,
        url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        ...(recovered ? { recovered } : {}),
      });
    },
    [queryClient, refreshConfiguration, progressMutation.mutate, capture],
  );

  // (Re)arm the idle watchdog. Called when processing begins and on every progress
  // event, so a scrape that keeps streaming is never killed; only IDLE_TIMEOUT_MS of
  // real silence trips it. On fire, reconcile once against the server row — advance if
  // the scrape finished, else surface the recoverable error instead of an infinite spinner.
  const resetIdleWatchdog = useCallback(() => {
    if (stateRef.current !== 'processing') return;
    if (idleWatchdogTimerRef.current !== null) clearTimeout(idleWatchdogTimerRef.current);
    idleWatchdogTimerRef.current = setTimeout(() => {
      if (stateRef.current !== 'processing') return;
      void queryClient.refetchQueries({ queryKey: [QueryKey.gettingStarted.progress] }).then(() => {
        if (stateRef.current !== 'processing') return;
        const fresh = queryClient.getQueryData<GettingStartedProgressDto | null>([
          QueryKey.gettingStarted.progress,
        ]);
        if (fresh?.knowledge_base_step_completed) {
          applyCompletion('timeout');
          return;
        }
        setState('error');
        setErrorCategory('timeout');
        capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
          error_message: 'no_terminal_event',
          error_class: 'backend',
          error_category: 'timeout',
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
      });
    }, IDLE_TIMEOUT_MS);
  }, [queryClient, applyCompletion, capture]);

  // Listen for websocket progress events
  useEffect(() => {
    const handler = (event: { step: string; data?: Record<string, string> }) => {
      // Accept events only when we've kicked the backend job off (idle right
      // after submit, processing while it runs). Once we've reached `completed`
      // or `error`, late events from a retry of a previous run must be ignored
      // so they don't overwrite the current state machine.
      const isAcceptingEvents = stateRef.current === 'processing' || stateRef.current === 'idle';
      if (!isAcceptingEvents) {
        // Still ignored by the state machine, but `ignored_in_state` keeps it out of the failure funnel.
        if (event.step === 'error') {
          capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
            error_message: event.data?.raw_message ?? event.data?.message ?? '',
            error_class: 'backend',
            error_category: parseBackendCategory(event.data?.category),
            ignored_in_state: stateRef.current,
            url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
          });
        }
        return;
      }

      if (event.step === 'started') {
        setState('processing');
      }

      if (event.step === 'url_type_detected' && event.data?.url_type) {
        const detected = event.data.url_type as 'shopify' | 'generic';
        setUrlType(detected);
        urlTypeRef.current = detected;
      }

      if (event.step === 'setting_applied') {
        settingsAppliedCountRef.current += 1;
        // Refresh sidebar when agent name or avatar changes
        if (event.data?.setting === 'ai_agent_name' || event.data?.setting === 'avatar') {
          queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
        }
      }

      // Non-terminal: the backend still finishes with a neutral persona, so we only
      // raise an advisory banner and let the stream continue to `completed`.
      if (event.step === 'low_quality_source') {
        const parsedReason = parseWarningReason(event.data?.reason);
        // Sync the ref in the same tick: socket.io can deliver this event and
        // `completed` in one batch, and applyCompletion reads the ref before
        // React re-renders the state mirror.
        warningReasonRef.current = parsedReason;
        setWarningReason(parsedReason);
        capture(PostHogEventEnum.GettingStartedAutomaticLowQuality, {
          reason: event.data?.reason ?? '',
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
      }

      if (event.step === 'error') {
        setState('error');
        // Backend sends `{category, raw_message}` — category drives the
        // localized copy, raw_message stays out of the UI entirely. Older
        // backends that sent `{message}` fall through to `unknown`; that's
        // fine since the copy is the same friendly fallback.
        const category = parseBackendCategory(event.data?.category);
        setErrorCategory(category);
        // `error_class: 'backend'` distinguishes a WS failure (request reached
        // the server, the background task failed) from the mutation onError
        // paths below (`network` / `http_*`). Same event name; the property
        // tells you where in the stack it died. `error_message` carries the
        // raw backend string for our own debugging in PostHog — never rendered.
        const rawMessage = event.data?.raw_message ?? event.data?.message ?? '';
        capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
          error_message: rawMessage,
          error_class: 'backend',
          error_category: category,
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
        return;
      }

      if (event.step === 'completed') {
        // New backends repeat the verdict on the terminal event. This closes
        // the reconnect window where `low_quality_source` was missed but the
        // later `completed` event arrived. Preserve the ref when the field is
        // absent so rolling deploys with an older backend keep using the
        // earlier event.
        if (event.data?.low_quality_reason !== undefined) {
          const completedReason = parseWarningReason(event.data.low_quality_reason);
          warningReasonRef.current = completedReason;
          setWarningReason(completedReason);
        }
        // The terminal payload carries the authoritative url_type; adopt it before gating the site-map review, since a reconnect may have missed the earlier `url_type_detected`.
        const completedType = event.data?.url_type;
        if (completedType === 'generic' || completedType === 'shopify') {
          urlTypeRef.current = completedType;
          setUrlType(completedType);
        }
        applyCompletion();
      }

      setSteps((prev) => [...prev, { step: event.step, data: event.data }]);
      // Every non-terminal event is a sign of life — re-arm the idle watchdog. `error`
      // returns early above and `completed` has already flipped `stateRef`, so both are
      // no-ops here (the guard inside `resetIdleWatchdog` bails when not processing).
      resetIdleWatchdog();
    };

    socket.on(SocketKey.onboarding.fromUrlProgress, handler);
    return () => {
      socket.off(SocketKey.onboarding.fromUrlProgress, handler);
    };
  }, [socket, queryClient, capture, applyCompletion, resetIdleWatchdog]);

  // A reconnected socket can miss the one-shot `completed` event; on (re)connect while processing, reconcile against the server progress row (backend persists the flag on success) and advance if the scrape finished while we were away.
  useEffect(() => {
    const onConnect = () => {
      if (stateRef.current !== 'processing') return;
      void queryClient.refetchQueries({ queryKey: [QueryKey.gettingStarted.progress] }).then(() => {
        if (stateRef.current !== 'processing') return;
        const fresh = queryClient.getQueryData<GettingStartedProgressDto | null>([
          QueryKey.gettingStarted.progress,
        ]);
        if (fresh?.knowledge_base_step_completed) applyCompletion('reconnect');
      });
    };
    socket.on('connect', onConnect);
    return () => {
      socket.off('connect', onConnect);
    };
  }, [socket, queryClient, applyCompletion]);

  // Arm the idle watchdog when processing begins; each progress event re-arms it (see the
  // handler above), and leaving `processing` (or unmount) clears it.
  useEffect(() => {
    if (state !== 'processing') return;
    resetIdleWatchdog();
    return () => {
      if (idleWatchdogTimerRef.current !== null) {
        clearTimeout(idleWatchdogTimerRef.current);
        idleWatchdogTimerRef.current = null;
      }
    };
  }, [state, resetIdleWatchdog]);

  const mutation = useMutation({
    mutationKey: [QueryKey.onboarding.fromUrl],
    // `submitUrl` flips state to `processing` synchronously before the mutation
    // fires, so a POST failure (rate-limit 429, 400 invalid URL, 5xx) would
    // otherwise strand the UI on the progress card forever — no WS events
    // arrive because the backend job was never dispatched. Mirror the WS
    // `error` handler: transition to `error`, surface the backend message
    // (falling back to null so the view can localize), and fire the same
    // PostHog failure event. The `isAcceptingEvents` guard in the WS handler
    // then drops any stale events from a prior run.
    onError: (error) => {
      const {
        message,
        errorClass: cls,
        errorCategory: cat,
        httpStatus,
      } = classifyMutationError(error);
      setState('error');
      setErrorCategory(cat);
      capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
        error_message: message ?? '',
        error_class: cls,
        error_category: cat,
        // Only attach when present — undefined fields are dropped by PostHog so
        // the network-failure case doesn't pollute the property with a literal.
        http_status: httpStatus,
        url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
      });
    },
  });

  const submitUrl = useCallback(
    (url: string) => {
      setState('processing');
      setSteps([]);
      setErrorCategory(null);
      setWarningReason(null);
      setUrlType(null);
      setSubmittedUrl(url);
      settingsAppliedCountRef.current = 0;
      submittedUrlRef.current = url;
      urlTypeRef.current = null;
      // Commit the user's mode choice to the DB. The UI normalizes `choice`
      // to `automatic` so this looks identical client-side, but production
      // funnel queries that filter by `mode='automatic'` were silently
      // missing every automatic-flow user (their DB row stayed `choice`).
      progressMutation.mutate({ mode: 'automatic' });
      // Deliberately NOT pre-registering the URL in the upload store: the
      // bottom-right scrape progress widget would compete for attention with
      // the onboarding flow itself. Once the user clicks through onboarding
      // and lands on the KB page, the page-header bar and sidebar warning
      // icon (driven by useKbUsageSocket → /allowed_features) surface the
      // KB-limit state if the post-onboarding sitewide crawl saturates the
      // plan limit — a quieter, contextual signal than a transient pill.
      capture(PostHogEventEnum.GettingStartedAutomaticSubmitted, {
        url_host: extractUrlHost(url),
      });
      mutation.mutate({ url });
    },
    [mutation, progressMutation, capture],
  );

  const reset = useCallback(() => {
    setState('idle');
    setSteps([]);
    setErrorCategory(null);
    warningReasonRef.current = null;
    setWarningReason(null);
    setUrlType(null);
  }, []);

  return {
    state,
    steps,
    errorCategory,
    warningReason,
    urlType,
    submittedUrl,
    submitUrl,
    isSubmitting: mutation.isPending,
    reset,
  };
}
